import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// Shadcn UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

// Icons
import { 
  Package2, DollarSign, TrendingUp, Search, Filter, Download,
  BarChart3, PieChart, AlertTriangle, ArrowUp, ArrowDown
} from 'lucide-react';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;
const MOCK_TENANT_ID = 'tenant-123';

// APPROVED CATEGORIES - Single source of truth from Category Reference sheet
const APPROVED_CATEGORIES = [
  'Buckets',
  'Bowls',
  'Cheese Chews',
  'Wood',
  'Dog Toys',
  'Dog Food',
  'Raw Materials'
];

const InventoryDashboard = () => {
  console.log('🔧 InventoryDashboard component rendered');
  
  // State Management
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    warehouse: 'all',
    product: '',
    dateRange: '30d'
  });

  // Sample warehouse data with icons
  const warehouses = [
    { value: 'all', label: 'All Warehouses', icon: '' },
    { value: 'fba', label: '🟣 FBA (Amazon)', icon: '🟣' },
    { value: 'awd', label: '🟢 AWD (Walmart)', icon: '🟢' },
    { value: 'garage', label: '🔵 Garage (Internal)', icon: '🔵' }
  ];

  // Build categories list ONLY from approved categories (no API calls, no dynamic data)
  const categories = [
    { value: 'all', label: 'All Categories' },
    ...APPROVED_CATEGORIES.map(cat => ({
      value: cat,
      label: cat
    }))
  ];
  
  console.log('🔍 INVENTORY DASHBOARD - Categories for dropdown:', categories);

  // Fetch inventory data
  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      // Use the existing product-master endpoint to get inventory data
      const response = await axios.get(`${API}/product-master/list?tenant_id=${MOCK_TENANT_ID}&page_size=100`);
      
      console.log('🔍 INVENTORY DASHBOARD - First Item Raw Data:', response.data.items[0]);
      
      // Log ALL cost-related fields for debugging
      console.log('💰 COST FIELD DEBUG - First 3 items (using COGS for valuation):');
      response.data.items.slice(0, 3).forEach(item => {
        console.log(`  SKU ${item.sku}:`, {
          'pricing_v2.total_cost (COGS)': item.pricing_v2?.total_cost,
          cost_price: item.cost_price,
          effective_cost: item.effective_cost,
          wholesale_price: item.wholesale_price,
          retail_price: item.retail_price,
          price: item.price,
          'pricing_v2': item.pricing_v2,
          'pricing_v2?.wholesale_price': item.pricing_v2?.wholesale_price,
          'pricing_v2?.retail_price': item.pricing_v2?.retail_price,
          'pricing_v2?.manufacturing_cost': item.pricing_v2?.manufacturing_cost?.total,
          'inventory.current_stock': item.inventory?.current_stock,
          current_stock: item.current_stock,
          on_hand: item.on_hand
        });
      });
      
      // Transform the data to include real inventory calculations
      const transformedData = response.data.items.map(item => {
        // CRITICAL FIX: Use inventory.current_stock from new consolidated structure, fallback to old fields
        const qtyOnHand = item.inventory?.current_stock ?? item.current_stock ?? item.planning?.on_hand ?? item.on_hand ?? 0;
        
        // CRITICAL FIX: Use COGS (total_cost) from Pricing V2 for accurate inventory valuation
        // Total Cost = Manufacturing + Import/Shipping + Fulfillment + Marketing
        
        // Try to get saved total_cost first
        let unitCost = item.pricing_v2?.total_cost;
        
        // If not saved, calculate it from components
        if (!unitCost && item.pricing_v2) {
          const mfgCost = item.pricing_v2.manufacturing_cost || 0;
          const importCost = item.pricing_v2.import_shipping_cost || 0;
          const fulfillmentCost = item.pricing_v2.fulfillment_cost || 0;
          const marketingCost = item.pricing_v2.marketing_cost || 0;
          
          if (mfgCost > 0 || importCost > 0 || fulfillmentCost > 0 || marketingCost > 0) {
            unitCost = mfgCost + importCost + fulfillmentCost + marketingCost;
            console.log(`💡 Calculated COGS for ${item.sku}: ${unitCost.toFixed(2)} (not saved in total_cost)`);
          }
        }
        
        // Final fallback chain if no pricing_v2 data
        if (!unitCost) {
          unitCost = item.pricing_v2?.wholesale_price ?? 
                    item.pricing_v2?.manufacturing_cost?.total ??
                    item.cost_price ?? 
                    item.effective_cost ?? 
                    item.wholesale_price ?? 
                    item.retail_price ?? 
                    item.price ?? 
                    0;
        }
        
        // Calculate total value using COGS
        const totalValue = qtyOnHand * unitCost;
        
        console.log(`📊 SKU ${item.sku} CALCULATION:`, {
          qtyOnHand,
          unitCost,
          totalValue,
          'pricing_v2 exists': !!item.pricing_v2,
          'pricing_v2.total_cost': item.pricing_v2?.total_cost,
          'pricing_v2.manufacturing_cost': item.pricing_v2?.manufacturing_cost,
          'pricing_v2.import_shipping_cost': item.pricing_v2?.import_shipping_cost,
          'pricing_v2.fulfillment_cost': item.pricing_v2?.fulfillment_cost,
          'pricing_v2.marketing_cost': item.pricing_v2?.marketing_cost,
          'pricing_v2.wholesale_price': item.pricing_v2?.wholesale_price,
          'cost used': item.pricing_v2?.total_cost ? 'pricing_v2.total_cost (COGS)' :
                      item.pricing_v2?.wholesale_price ? 'pricing_v2.wholesale_price' :
                      item.pricing_v2?.manufacturing_cost?.total ? 'pricing_v2.manufacturing_cost.total' :
                      item.cost_price ? 'cost_price' : 
                      item.effective_cost ? 'effective_cost' :
                      item.wholesale_price ? 'wholesale_price' :
                      item.retail_price ? 'retail_price' :
                      item.price ? 'price' : 'NONE (0)'
        });
        
        // Assign warehouse based on product category for realistic distribution
        // CRITICAL FIX: Use category_id instead of category
        const categoryValue = item.category_id || item.category;
        let warehouse;
        switch(categoryValue) {
          case 'Bowls':
            warehouse = 'fba';
            break;
          case 'Dog Toys':
            warehouse = 'awd';
            break;
          case 'Cheese Chews':
            warehouse = 'garage';
            break;
          default:
            warehouse = getRandomWarehouse();
        }
        
        return {
          ...item,
          warehouse,
          qty_on_hand: qtyOnHand,
          unit_cost: unitCost,
          total_value: totalValue
        };
      });
      
      setInventoryData(transformedData);
      console.log('📊 Inventory data loaded:', transformedData.length, 'items');
    } catch (error) {
      console.error('Error fetching inventory data:', error);
      // Set empty data on error to prevent crashes
      setInventoryData([]);
    } finally {
      setLoading(false);
    }
  };

  // Mock warehouse assignment for demo
  const getRandomWarehouse = () => {
    const options = ['fba', 'awd', 'garage'];
    return options[Math.floor(Math.random() * options.length)];
  };

  // Filtered data based on current filters
  const filteredData = useMemo(() => {
    return inventoryData.filter(item => {
      const matchesCategory = filters.category === 'all' || item.category === filters.category;
      const matchesWarehouse = filters.warehouse === 'all' || item.warehouse === filters.warehouse;
      const matchesProduct = !filters.product || 
        item.name?.toLowerCase().includes(filters.product.toLowerCase()) ||
        item.sku?.toLowerCase().includes(filters.product.toLowerCase());
      
      return matchesCategory && matchesWarehouse && matchesProduct && item.qty_on_hand > 0;
    });
  }, [inventoryData, filters]);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalUnits = filteredData.reduce((sum, item) => sum + item.qty_on_hand, 0);
    const totalValue = filteredData.reduce((sum, item) => sum + item.total_value, 0);
    const activeSKUs = filteredData.filter(item => item.qty_on_hand > 0).length;
    const totalSKUs = inventoryData.length; // All SKUs, not just filtered
    
    // DEBUG: Log KPI calculations
    console.log('📈 KPI CALCULATIONS:', {
      totalUnits,
      totalValue,
      activeSKUs,
      totalSKUs,
      'Sample items': filteredData.slice(0, 3).map(item => ({
        sku: item.sku,
        qty_on_hand: item.qty_on_hand,
        unit_cost: item.unit_cost,
        total_value: item.total_value
      }))
    });
    
    // Top 5 SKUs by value
    const topSkus = [...filteredData]
      .sort((a, b) => b.total_value - a.total_value)
      .slice(0, 5);
    
    // Top 5 categories by value (more relevant for business)
    const categoryValues = {};
    filteredData.forEach(item => {
      categoryValues[item.category] = (categoryValues[item.category] || 0) + item.total_value;
    });
    const topCategories = Object.entries(categoryValues)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([category, value]) => ({ category, value }));

    // Low stock alerts (items with less than 10 units)
    const lowStockItems = filteredData.filter(item => item.qty_on_hand > 0 && item.qty_on_hand < 10);
    
    // Out of stock items
    const outOfStockItems = inventoryData.filter(item => item.qty_on_hand === 0);

    return {
      totalUnits,
      totalValue,
      totalSKUs,
      activeSKUs,
      topSkus,
      topCategories,
      lowStockItems,
      outOfStockItems,
      avgValue: totalUnits > 0 ? totalValue / totalUnits : 0,
      stockingRate: totalSKUs > 0 ? (activeSKUs / totalSKUs) * 100 : 0
    };
  }, [filteredData, inventoryData]);

  // Warehouse breakdown for pie chart
  const warehouseBreakdown = useMemo(() => {
    const breakdown = {};
    filteredData.forEach(item => {
      const warehouse = item.warehouse || 'unknown';
      breakdown[warehouse] = (breakdown[warehouse] || 0) + item.total_value;
    });
    return breakdown;
  }, [filteredData]);

  // Load data on component mount
  useEffect(() => {
    fetchInventoryData();
  }, []);

  return (
    <div className="max-w-none w-full space-y-6">
      {/* Debug Indicator */}
      <div style={{backgroundColor: 'green', color: 'white', padding: '10px', fontWeight: 'bold'}}>
        🎯 INVENTORY DASHBOARD: Component loaded successfully
      </div>
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventory Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time inventory tracking by product, category, and warehouse</p>
          <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button onClick={fetchInventoryData} disabled={loading}>
            <TrendingUp className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Category Filter - NATIVE SELECT (matches Product Detail page) */}
            <div>
              <label className="text-sm font-medium mb-2 block">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Warehouse Filter - NATIVE SELECT (matches Product Detail page) */}
            <div>
              <label className="text-sm font-medium mb-2 block">Warehouse</label>
              <select
                value={filters.warehouse}
                onChange={(e) => setFilters(prev => ({ ...prev, warehouse: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              >
                {warehouses.map(wh => (
                  <option key={wh.value} value={wh.value}>{wh.label}</option>
                ))}
              </select>
            </div>

            {/* Product Search */}
            <div>
              <label className="text-sm font-medium mb-2 block">Product Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search SKU or product name..."
                  value={filters.product}
                  onChange={(e) => setFilters(prev => ({ ...prev, product: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Date Range - NATIVE SELECT (matches Product Detail page) */}
            <div>
              <label className="text-sm font-medium mb-2 block">Date Range</label>
              <select
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days (Default)</option>
                <option value="90d">Last 90 days</option>
                <option value="ytd">Year to date</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards - Reordered */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${kpis.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <p className="text-xs text-muted-foreground">
              Current inventory valuation
            </p>
          </CardContent>
        </Card>

        {/* Total Units */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
            <Package2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.totalUnits.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Across all locations
            </p>
          </CardContent>
        </Card>

        {/* Average Value per Unit */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Value/Unit</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${kpis.avgValue.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              Weighted average cost
            </p>
          </CardContent>
        </Card>

        {/* Active SKUs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active SKUs</CardTitle>
            <div className="flex items-center gap-1">
              {kpis.lowStockItems.length > 0 && (
                <AlertTriangle className="h-4 w-4 text-orange-500" />
              )}
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.activeSKUs}</div>
            <p className="text-xs text-muted-foreground">
              {kpis.lowStockItems.length > 0 
                ? `${kpis.lowStockItems.length} low stock alerts`
                : `${kpis.stockingRate.toFixed(1)}% stocking rate`
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Warehouse Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Inventory by Warehouse</CardTitle>
            <CardDescription>Distribution of inventory value across locations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(warehouseBreakdown).map(([warehouse, value]) => {
                const percentage = ((value / kpis.totalValue) * 100) || 0;
                return (
                  <div key={warehouse} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${
                        warehouse === 'fba' ? 'bg-blue-500' :
                        warehouse === 'awd' ? 'bg-green-500' :
                        warehouse === 'garage' ? 'bg-orange-500' : 'bg-gray-500'
                      }`} />
                      <span className="text-sm capitalize">{warehouse}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">${value.toLocaleString()}</span>
                      <Badge variant="secondary">{percentage.toFixed(1)}%</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Categories */}
        <Card>
          <CardHeader>
            <CardTitle>Top Categories by Units</CardTitle>
            <CardDescription>Highest volume inventory categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {kpis.topCategories.map((item, index) => (
                <div key={item.category} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                    <span className="text-sm capitalize">{item.category}</span>
                  </div>
                  <Badge variant="outline">{item.units} units</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inventory Table */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory Details</CardTitle>
          <CardDescription>
            {filteredData.length} products • ${kpis.totalValue.toLocaleString()} total value
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">COGS</TableHead>
                  <TableHead className="text-right">Total Value</TableHead>
                  <TableHead className="text-right">% of Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <TableCell key={j}>
                          <div className="h-4 bg-gray-200 rounded animate-pulse" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                      No inventory data found for current filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.slice(0, 20).map((item) => {
                    const percentOfTotal = ((item.total_value / kpis.totalValue) * 100) || 0;
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="font-mono text-sm">{item.sku}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {item.category_id || item.category || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${
                            item.warehouse === 'fba' ? 'bg-blue-100 text-blue-800' :
                            item.warehouse === 'awd' ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {item.warehouse?.toUpperCase() || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{item.qty_on_hand}</TableCell>
                        <TableCell className="text-right">${item.unit_cost?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell className="text-right font-medium">
                          ${item.total_value.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary">{percentOfTotal.toFixed(1)}%</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.qty_on_hand < 10 ? 'destructive' : 'default'}>
                            {item.qty_on_hand < 10 ? 'Low Stock' : 'In Stock'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryDashboard;